import { useCallback, useMemo, useState } from 'react';
import { useWizard } from 'react-use-wizard';
import { Check } from 'lucide-react';

import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { cn } from '@client/lib';

import {
  ASSET_TYPES,
  ASSET_TYPE_TO_ACCOUNT_TYPE,
  filterForRetirementIncomeExpanded,
  type AssetTypeId,
} from '@/features/guided-financial-planning/config';
import {
  useSession,
  useSessionMutations,
  useIsSessionComplete,
} from '@/features/guided-financial-planning/context';
import { useFundingSources } from '@/features/guided-financial-planning/hooks/use-funding-sources';
import { StepLayout } from '@/features/guided-financial-planning/components/step-layout';

interface AssetCardProps {
  id: AssetTypeId;
  label: string;
  isSelected: boolean;
  onToggle: (id: AssetTypeId) => void;
  disabled?: boolean;
}

function AssetCard({ id, label, isSelected, onToggle, disabled }: AssetCardProps) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onToggle(id)}
      disabled={disabled}
      className={cn(
        'relative flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all duration-200',
        disabled ? 'cursor-not-allowed opacity-60' : 'hover:border-primary/50 hover:bg-primary/5',
        isSelected ? 'border-primary bg-primary/5' : 'border-gray-200 bg-white',
      )}
    >
      <div
        className={cn(
          'flex size-6 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-200',
          isSelected ? 'border-primary bg-primary text-white' : 'border-gray-300 bg-white',
        )}
      >
        {isSelected && <Check className="size-4" />}
      </div>
      <span className="font-medium text-gray-900">{label}</span>
    </button>
  );
}

export function AssetSelectionStep() {
  const { nextStep } = useWizard();
  const { session } = useSession();
  const { updateStepData, isSaving } = useSessionMutations();
  const isSessionComplete = useIsSessionComplete();
  const { flags } = useFeatureFlags();
  const fundingSources = useFundingSources();

  const sessionAssets = session?.stepData?.selectedAssets;
  const [localOverrides, setLocalOverrides] = useState<Set<AssetTypeId> | null>(null);

  const assetTypesToShow = useMemo(() => {
    const base = flags.existingLtcPolicyTypeOnCard
      ? ASSET_TYPES
      : ASSET_TYPES.filter(a => a.id !== 'existing-ltc-policy');
    return filterForRetirementIncomeExpanded(base, flags.retirementIncomeExpanded);
  }, [flags.existingLtcPolicyTypeOnCard, flags.retirementIncomeExpanded]);

  // Asset types that already have a funding source (added from funding table or elsewhere)
  const assetTypesWithFundingSource = useMemo(() => {
    const selfFunding = fundingSources?.selfFunding ?? {};
    const accountTypes = new Set(
      Object.values(selfFunding)
        .map(s => s.accountType)
        .filter(Boolean) as string[],
    );
    const result = new Set<AssetTypeId>();
    for (const [assetId, accountType] of Object.entries(ASSET_TYPE_TO_ACCOUNT_TYPE)) {
      if (accountTypes.has(accountType)) {
        result.add(assetId as AssetTypeId);
      }
    }
    return result;
  }, [fundingSources?.selfFunding]);

  const sessionAssetsSet = useMemo(
    () => new Set<AssetTypeId>(sessionAssets ?? []),
    [sessionAssets],
  );

  // When user hasn't toggled anything yet, merge session + existing funding sources.
  // Once user starts toggling, respect their explicit choices.
  const effectiveSelectedAssets = useMemo(() => {
    if (localOverrides) return localOverrides;
    const merged = new Set(sessionAssetsSet);
    assetTypesWithFundingSource.forEach(id => merged.add(id));
    return merged;
  }, [localOverrides, sessionAssetsSet, assetTypesWithFundingSource]);

  const handleToggle = useCallback(
    (id: AssetTypeId) => {
      const current = localOverrides ?? new Set([...sessionAssetsSet, ...assetTypesWithFundingSource]);
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      setLocalOverrides(next);
    },
    [localOverrides, sessionAssetsSet, assetTypesWithFundingSource],
  );

  const handleNext = useCallback(async () => {
    const selectedArray = Array.from(effectiveSelectedAssets) as AssetTypeId[];
    await updateStepData({ selectedAssets: selectedArray });
    nextStep();
  }, [effectiveSelectedAssets, updateStepData, nextStep]);

  const hasSelection = effectiveSelectedAssets.size > 0;

  return (
    <StepLayout
      title="What assets do you have?"
      subtitle="Select all the accounts and assets you currently own. We'll use this to assess your long-term care coverage capacity."
      isNextDisabled={!hasSelection || isSaving}
      nextButtonLabel="Continue"
      onNext={handleNext}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {assetTypesToShow.map(asset => (
          <AssetCard
            key={asset.id}
            id={asset.id}
            label={asset.label}
            isSelected={effectiveSelectedAssets.has(asset.id)}
            onToggle={handleToggle}
            disabled={isSessionComplete}
          />
        ))}
      </div>
    </StepLayout>
  );
}
